'use client';

import { forwardRef, createContext, useContext } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

// Context for settings display options
interface SettingsDisplayContextValue {
  hideIcons?: boolean;
}

const SettingsDisplayContext = createContext<SettingsDisplayContextValue>({});

export function SettingsDisplayProvider({
  children,
  hideIcons = false,
}: {
  children: React.ReactNode;
  hideIcons?: boolean;
}) {
  return (
    <SettingsDisplayContext.Provider value={{ hideIcons }}>
      {children}
    </SettingsDisplayContext.Provider>
  );
}

export function useSettingsDisplay() {
  return useContext(SettingsDisplayContext);
}

interface SettingsSectionRowProps {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  title: string;
  subtitle?: string | string[];
  /** Rich inline subtitle (e.g. reference thumbnail strip). Takes precedence
   *  over the plain-text `subtitle` in the trailing slot when provided. */
  subtitleContent?: React.ReactNode;
  hasChanges?: boolean;
  onClick?: () => void;
  className?: string;
  /** Optional element rendered right next to the title (e.g. a PRO badge). */
  titleAdornment?: React.ReactNode;
  /** Orange dot signalling the row needs user attention (e.g. a required
   *  field is empty). Mirrors the iOS/Android indicator. */
  needsAttention?: boolean;
}

/**
 * Unified settings row component - iOS/Android style menu item
 * Used by all settings sections for consistent design
 */
export const SettingsSectionRow = forwardRef<
  HTMLDivElement,
  SettingsSectionRowProps
>(
  (
    {
      icon: Icon,
      title,
      subtitle,
      subtitleContent,
      hasChanges = false,
      onClick,
      className,
      titleAdornment,
      needsAttention = false,
    },
    ref
  ) => {
    const { hideIcons } = useSettingsDisplay();

    return (
      <div
        ref={ref}
        className={cn(
          'cursor-pointer hover:bg-foreground/5 active:bg-foreground/10 transition-colors',
          className
        )}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.();
          }
        }}
      >
        <div className="flex items-center gap-3 py-3 px-4">
          {!hideIcons && Icon && (
            <Icon
              className="h-5 w-5 text-primary flex-shrink-0"
              strokeWidth={1.5}
            />
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-card-body text-foreground whitespace-nowrap">
                {title}
              </span>

              {titleAdornment}

              {hasChanges && (
                <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
              )}

              {needsAttention && (
                <span className="w-2 h-2 rounded-full bg-warning flex-shrink-0" />
              )}

              {subtitleContent ? (
                <div className="flex-1 min-w-0 flex justify-end">
                  {subtitleContent}
                </div>
              ) : (
                subtitle &&
                !Array.isArray(subtitle) && (
                  <span className="text-list-item-subtitle text-muted-foreground flex-1 min-w-0 truncate text-right">
                    {subtitle}
                  </span>
                )
              )}
            </div>

            {subtitle && Array.isArray(subtitle) && (
              <div className="flex flex-col gap-1.5 mt-1">
                {subtitle.map((line, i) => (
                  <p
                    key={i}
                    className="text-list-item-subtitle text-muted-foreground line-clamp-2"
                  >
                    {line}
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* Apple parity: SettingsRowButton renders a trailing
              `chevron.forward` to signal the row pushes a detail panel. */}
          <ChevronRight
            className="h-4 w-4 text-muted-foreground/70 flex-shrink-0 rtl:-scale-x-100"
            strokeWidth={2.5}
          />
        </div>
      </div>
    );
  }
);

SettingsSectionRow.displayName = 'SettingsSectionRow';

// Legacy SettingsSection for backward compatibility
interface SettingsSectionProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle?: string | string[];
  hasChanges?: boolean;
  onClick?: () => void;
  children?: React.ReactNode;
  className?: string;
  showChevron?: boolean;
  compact?: boolean;
  hideIcon?: boolean;
}

export const SettingsSection = forwardRef<HTMLDivElement, SettingsSectionProps>(
  (
    {
      icon: Icon,
      title,
      subtitle,
      hasChanges = false,
      onClick,
      children,
      className,
      showChevron: _showChevron = false,
      compact = false,
      hideIcon = false,
    },
    ref
  ) => {
    const isClickable = !!onClick;
    const { hideIcons: hideIconsFromContext } = useSettingsDisplay();
    const shouldHideIcon = hideIcon || hideIconsFromContext;

    return (
      <div
        ref={ref}
        className={cn(
          'border-b border-border/40 last:border-b-0',
          isClickable && 'cursor-pointer hover:bg-foreground/5 active:bg-foreground/10',
          className
        )}
        onClick={onClick}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onKeyDown={(e) => {
          if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClick?.();
          }
        }}
      >
        <div
          className={cn(
            'flex items-center gap-3',
            compact ? 'px-4 py-3' : 'px-4 py-3'
          )}
        >
          {!shouldHideIcon && (
            <div className="flex-shrink-0 w-10 h-10 rounded-[10px] bg-muted flex items-center justify-center">
              <Icon className="w-5 h-5 text-muted-foreground" />
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-list-item-title text-foreground">
                {title}
              </span>
              {hasChanges && (
                <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
              )}
              <span className="flex-1" />
              {subtitle && !Array.isArray(subtitle) && (
                <span className="text-list-item-subtitle text-muted-foreground max-w-[120px] truncate">
                  {subtitle}
                </span>
              )}
            </div>

            {subtitle && Array.isArray(subtitle) && (
              <div className="flex flex-col gap-1.5 mt-1">
                {subtitle.map((line, i) => (
                  <p
                    key={i}
                    className="text-list-item-subtitle text-muted-foreground line-clamp-2"
                  >
                    {line}
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* Apple-parity: legacy `showChevron` flag intentionally ignored —
              settings rows have no trailing chevron anywhere on Apple/Android. */}
        </div>

        {children && <div className="px-4 pb-4">{children}</div>}
      </div>
    );
  }
);

SettingsSection.displayName = 'SettingsSection';
